import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Grid, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useTranslation } from 'react-i18next';
import { CONTENT_TOP_OFFSET } from '../../layout/layoutMetrics';
import Colors from '../../components/ui/Colors';
import { hasAnyPermission, permissionsLoaded } from '../../components/AccessControl';
import {
  createDashboard, DASHBOARD_MOCK_MODE, deleteDashboard, listDashboards,
} from '../../utilityFunctions/dashboardApi';
import CreateDashboardDialog from './nl/CreateDashboardDialog';

/** Lenient like the menu: when no permission set resolved, show the action; the server re-checks. */
export const canDo = (loginUser, perms) => !permissionsLoaded(loginUser) || hasAnyPermission(loginUser, perms);

const formatDate = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang && lang.startsWith('es') ? 'es-CO' : 'en-US',
      { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return iso;
  }
};

export default function DashboardList({ loginUser }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const jwt = loginUser && loginUser.jwtToken;

  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [toDelete, setToDelete] = React.useState(null);
  const [aiOpen, setAiOpen] = React.useState(false);

  const canCreate = canDo(loginUser, ['DASHBOARD_CREATE']);
  const canEdit = canDo(loginUser, ['DASHBOARD_EDIT']);
  const myEmail = loginUser && (loginUser.email || loginUser.username || loginUser.accountId);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await listDashboards(jwt);
    setLoading(false);
    if (res.ok && Array.isArray(res.data)) {
      setItems(res.data.slice().sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || ''))));
    } else {
      setError(t('Dashboards.list.loadFailed'));
    }
  }, [jwt, t]);

  React.useEffect(() => { load(); }, [load]);

  const open = (id) => history.push(`/data_analytics/dashboards/${id}`);

  const doCreate = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setBusy(true);
    const res = await createDashboard(title, jwt);
    setBusy(false);
    const created = res.ok && res.data && (res.data.dashboard || res.data);
    if (created && created.id != null) {
      setCreateOpen(false);
      setNewTitle('');
      open(created.id);
    } else {
      setError(t('Dashboards.list.createFailed'));
      setCreateOpen(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    const res = await deleteDashboard(toDelete.id, jwt);
    setBusy(false);
    setToDelete(null);
    if (res.ok) load();
    else setError(t('Dashboards.list.deleteFailed'));
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pt: CONTENT_TOP_OFFSET, pb: 3, maxWidth: 1200, mx: 'auto', boxSizing: 'border-box', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>{t('Dashboards.list.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('Dashboards.list.subtitle')}</Typography>
        </Box>
        {DASHBOARD_MOCK_MODE && <Chip color="secondary" size="small" label={t('Dashboards.view.mockMode')} />}
        {canCreate && (
          <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={() => setAiOpen(true)}
            sx={{ bgcolor: Colors.primary, '&:hover': { bgcolor: Colors.primary, opacity: 0.9 } }}>
            {t('Dashboards.create.button')}
          </Button>
        )}
        <CreateDashboardDialog
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          jwt={jwt}
          onStarted={({ dashboardId, runId }) => {
            setAiOpen(false);
            history.push(`/data_analytics/dashboards/${dashboardId}?run=${runId}`);
          }}
        />
        {canCreate && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            {t('Dashboards.list.newBlank')}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}

      {!loading && items.length === 0 && !error && (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <DashboardOutlinedIcon sx={{ fontSize: 56, color: Colors.primary }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>{t('Dashboards.list.emptyTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('Dashboards.list.emptyBody')}</Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        {items.map((d) => {
          const shared = String(d.visibility || '').toUpperCase() === 'TENANT';
          const mine = !d.owner_email || !myEmail || d.owner_email === myEmail;
          return (
            <Grid item xs={12} sm={6} md={4} key={d.id}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', position: 'relative' }}>
                <CardActionArea onClick={() => open(d.id)} sx={{ height: '100%', alignItems: 'flex-start' }}>
                  <CardContent sx={{ minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, pr: 4 }}>
                      <DashboardOutlinedIcon sx={{ color: Colors.primary }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0 }} noWrap title={d.title}>{d.title}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip size="small" icon={shared ? <GroupsOutlinedIcon /> : <LockOutlinedIcon />}
                        label={shared ? t('Dashboards.meta.visibilityTenant') : t('Dashboards.meta.visibilityPrivate')} />
                      <Chip size="small" variant="outlined" label={t('Dashboards.list.widgetCount', { count: d.widget_count || 0 })} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('Dashboards.list.updated', { date: formatDate(d.updated_at, i18n.language) })}
                    </Typography>
                    {d.owner_email && !mine && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('Dashboards.list.owner', { owner: d.owner_email })}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
                {canEdit && mine && (
                  <Tooltip title={t('Dashboards.list.delete')}>
                    <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8 }}
                      onClick={(e) => { e.stopPropagation(); setToDelete(d); }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={createOpen} onClose={() => !busy && setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('Dashboards.list.newBlank')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" label={t('Dashboards.meta.titleLabel')} value={newTitle}
            inputProps={{ maxLength: 200 }}
            onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doCreate(); }} />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setCreateOpen(false)}>{t('Dashboards.common.cancel')}</Button>
          <Button variant="contained" disabled={busy || !newTitle.trim()} onClick={doCreate}>
            {busy ? <CircularProgress size={18} /> : t('Dashboards.common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toDelete} onClose={() => !busy && setToDelete(null)}>
        <DialogTitle>{t('Dashboards.list.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{toDelete && t('Dashboards.list.deleteBody', { title: toDelete.title })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setToDelete(null)}>{t('Dashboards.common.cancel')}</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={doDelete}>{t('Dashboards.list.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
